import { css, html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import type { TopSubmenu } from './top-submenu';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { t } from 'src/i18n';
import 'src/i18n/d-language-selector';

/**
 *
 */
@customElement('d-top-submenu')
export class DTopSubmenu extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    #backdrop {
      position: fixed;
      top: var(--safe-area-inset-top);
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 100;
    }
    #submenu {
      position: relative;
      width: fit-content;
      max-width: 100vw;
      max-height: calc(100vh - 58px);
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
      box-shadow: 0 0 10px hsla(0, 0%, 0%, 0.3);
      padding: 13px 20px 12px;
      background: white;
    }
    .header {
      border-bottom: 1px solid var(--borderColor);
      padding-bottom: 10px;
      color: var(--text-color);
      font-size: 17px;
      font-weight: 500;
    }
    .items {
      padding-top: 6px;
    }
    .items > div,
    .items > a {
      display: block;
      margin-top: 1px;
      padding: 6px 0px;
      color: var(--linkColorGray);
      text-decoration: none;
      opacity: 0.8;
      cursor: pointer;
    }
    .items a.sub-item {
      padding-left: 8px;
    }
    .items > div.sub-header {
      color: var(--text-color);
      cursor: default;
    }
    .items d-language-selector {
      display: block;
      width: 125px;
      padding: 8px 0 2px;
    }
    @media (hover: hover) {
      .items > div:not(.sub-header):hover,
      .items > a:hover {
        color: var(--themeColorDarkerOne);
        opacity: 1;
      }
    }
  `;

  @property({ type: String })
  submenu: TopSubmenu = undefined;
  @property({ type: Number })
  position = 0;
  @property({ type: Number })
  appWidth = 0;
  @property({ type: String })
  currentOrganizationName = '';
  @property({ type: String })
  displayedUserName = '';
  @property({ type: Boolean })
  currentUserIsOwner = false;
  @property({ type: Boolean })
  currentUserHasMultipleOrganizations = false;
  /** Språkvelgeren vises kun for kunder med hseProduct (HMS-produktet). */
  @property({ type: Boolean })
  hseProduct = false;
  @property({ type: Array })
  lastOrganizationsLoadedByUser: { id: number; name: string }[] = [];
  @query('#submenu')
  submenuElm: HTMLDivElement | undefined;

  private get width(): number {
    if (this.submenuElm) {
      return this.submenuElm.getBoundingClientRect().width;
    }
    return 0;
  }

  private get left() {
    if (this.position + this.width > this.appWidth) {
      return this.appWidth - this.width;
    }
    return this.position;
  }

  private setStyles() {
    if (this.submenuElm) {
      this.submenuElm.style.left = this.left + 'px';
      if (this.left === 0) {
        this.submenuElm.style.borderBottomLeftRadius = '0';
      }
      if (this.appWidth - (this.left + this.width) === 0) {
        this.submenuElm.style.borderBottomRightRadius = '0';
      }
    }
  }

  private openSettings(settingsType: string) {
    this.dispatchEvent(
      new CustomEvent('open-settings', {
        bubbles: true,
        composed: true,
        detail: { settingsType },
      }),
    );
  }

  private logout() {
    this.dispatchEvent(new CustomEvent('request-logout', { bubbles: true, composed: true }));
  }

  private close() {
    this.dispatchEvent(
      new CustomEvent<{ submenu: string; position: number }>('close-top-submenu', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.setStyles();
  }

  renderOrganizations() {
    if (this.lastOrganizationsLoadedByUser.length > 0) {
      return html`
        <div class="sub-header">${t('topMenu.recentOrganizations')}</div>
        ${this.lastOrganizationsLoadedByUser.map((o) => {
          return html`<a class="sub-item" href="/account/${o.id}/${pageIds.status}">${o.name}</a>`;
        })}
        <div @click=${() => this.openSettings('organizations')}>${t('topMenu.allOrganizations')}</div>
      `;
    }
    return html` <div @click=${() => this.openSettings('organizations')}>${t('topMenu.myOrganizations')}</div> `;
  }

  renderSubmenu(submenu: string): TemplateResult<1> | typeof nothing {
    switch (submenu) {
      case 'new':
        return html`
          <div class="header">${t('topMenu.addNewElement')}</div>
          <slot name="new-document"></slot>
        `;
      case 'organization':
        return html`
          <div class="header">${this.currentOrganizationName}</div>
          <div class="items">
            ${this.currentUserIsOwner
              ? html` <div @click=${() => this.openSettings('subscription')}>${t('topMenu.orgOptions')}</div>
                  <div @click=${() => this.openSettings('users')}>${t('topMenu.users')}</div>`
              : nothing}
            ${this.currentUserHasMultipleOrganizations ? html` ${this.renderOrganizations()} ` : nothing}
          </div>
        `;
      case 'user':
        return html`
          <div class="header">${this.displayedUserName}</div>
          <div class="items">
            <div @click=${() => this.openSettings('profile')}>${t('topMenu.userOptions')}</div>
            <div @click=${() => this.logout()}>${t('topMenu.logout')}</div>
            ${this.hseProduct
              ? html`<d-language-selector .label=${t('language.label')}></d-language-selector>`
              : nothing}
          </div>
        `;
      default:
        return nothing;
    }
  }

  render(): TemplateResult<1> | typeof nothing {
    if (this.submenu) {
      return html`
        <div id="backdrop" @click=${this.close}>
          <div id="submenu">${this.renderSubmenu(this.submenu)}</div>
        </div>
      `;
    }
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-top-submenu': DTopSubmenu;
  }
}
